import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UploadCloud,
  FileSpreadsheet,
  CheckCircle2,
  ShieldCheck,
  DatabaseBackup,
  RotateCcw,
  Download,
} from "lucide-react";
import { api, money, dateFmt } from "../lib/api.js";
import { Card, Pill, Empty, Notice } from "../components/UI.jsx";
import PeriodFilter, { resolvePeriod } from "../components/PeriodFilter.jsx";
export default function ImportPage() {
  const navigate=useNavigate();
  const [accounts, setAccounts] = useState([]),
    [accountId, setAccountId] = useState(""),
    [file, setFile] = useState(null),
    [backupFile, setBackupFile] = useState(null),
    [sharingKey, setSharingKey] = useState(""),
    [preview, setPreview] = useState(null),
    [pdfMappings, setPdfMappings] = useState([]),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(null),
    [recurringSuggestions,setRecurringSuggestions]=useState(0),
    [period, setPeriod] = useState(() => ({
      mode: "all",
      ...resolvePeriod("all"),
      years: [],
      months: [],
    })),
    [exp, setExp] = useState({
      accountIds: [],
      format: "pisto",
      includeDefinitions: true,
      backupMode: "private",
      sharingKey: "",
    });
  useEffect(() => {
    api("/accounts").then((a) => {
      setAccounts(a);
      if (a[0]) setAccountId(a[0].id);
      setExp((x) => ({ ...x, accountIds: a.map((y) => Number(y.id)) }));
    });
  }, []);
  const fail = (e) => setNotice({ tone: "error", text: e.message });
  const upload = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (!/\.pdf$/i.test(file?.name || "")) fd.append("accountId", accountId);
      const result = await api("/import/preview", { method: "POST", body: fd });
      setPreview(result);
      if (result.multiAccount) {
        setPdfMappings(result.sections.map((section) => ({
          sourceId: section.sourceId,
          action: section.suggestedAccountId ? "existing" : "create",
          accountId: section.suggestedAccountId || accounts[0]?.id || "",
          name: `${section.name} ${section.maskedIdentifier}`,
          accountType: section.accountType,
          updateAccountDetails: false,
        })));
      } else setPdfMappings([]);
      setTimeout(() => document.getElementById("import-preview")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const r = await api("/import/commit", {
        method: "POST",
        body: preview.multiAccount
          ? { token: preview.token, mappings: pdfMappings }
          : { token: preview.token, includePossibleDuplicates: false },
      });
      const recurring=await api("/subscriptions/detect",{method:"POST",body:{}}).catch(()=>({suggestionsAdded:0}));
      setRecurringSuggestions(Number(recurring.suggestionsAdded||0));
      setNotice({
        tone: "success",
        text: `Imported ${r.imported} new movements; merged and cleared ${r.merged || 0} existing matches.${recurring.suggestionsAdded?` Pisto found ${recurring.suggestionsAdded} new recurring pattern${recurring.suggestionsAdded===1?"":"s"}.`:""}`,
      });
      setPreview(null);
      setPdfMappings([]);
      window.dispatchEvent(new Event("pisto:data-changed"));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const restore = async () => {
    if (
      !backupFile ||
      !confirm("Restore this Pisto backup? Exact duplicates will be skipped.")
    )
      return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", backupFile);
      if (sharingKey) fd.append("sharingKey", sharingKey);
      const r = await api("/import/pisto-backup", { method: "POST", body: fd });
      setNotice({
        tone: "success",
        text: `${r.mode === "shareable" ? "Shareable" : "Private"} backup restored: ${r.imported} imported, ${r.skipped} skipped.`,
      });
      setBackupFile(null);
      setSharingKey("");
      window.dispatchEvent(new Event("pisto:data-changed"));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const toggle = (id) =>
    setExp((x) => ({
      ...x,
      accountIds: x.accountIds.includes(id)
        ? x.accountIds.filter((v) => v !== id)
        : [...x.accountIds, id],
    }));
  const updatePdfMapping = (sourceId, change) =>
    setPdfMappings((items) => items.map((x) => x.sourceId === sourceId ? { ...x, ...change } : x));
  const download = async () => {
    try {
      if (!exp.accountIds.length)
        throw new Error("Choose at least one account.");
      if (
        exp.format === "pisto" &&
        exp.backupMode === "shareable" &&
        exp.sharingKey.length < 6
      )
        throw new Error("Export Sharing Key must be at least 6 characters.");
      setBusy(true);
      const body = {
          ...exp,
          dateFrom: period.from,
          dateTo: period.to,
          years: period.mode === "years" ? period.years : [],
          months: period.mode === "months" ? period.months : [],
        },
        r = await fetch("/api/export", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      if (!r.ok)
        throw new Error(
          (await r.json().catch(() => ({}))).error || "Export failed.",
        );
      const blob = await r.blob(),
        name =
          (r.headers.get("content-disposition") || "").match(
            /filename="([^"]+)"/,
          )?.[1] || "pisto-export",
        url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setNotice({ tone: "success", text: `Export created: ${name}` });
      setExp((x) => ({ ...x, sharingKey: "" }));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Import & Export</h1>
          <p>
            Bank statements, Excel-readable data, private backups and shareable
            Pisto backups.
          </p>
        </div>
      </div>
      {notice && <Notice tone={notice.tone}>{notice.text}</Notice>}
      {recurringSuggestions>0&&<Card><div className="recurring-import-prompt"><div><h2>Make repeated payments automatic?</h2><p>Pisto found recurring patterns in this import. Review the suggested frequency, next date and amount before creating planned movements.</p></div><button className="primary" onClick={()=>navigate("/subscriptions")}>Review recurring patterns</button></div></Card>}
      <div className="two-col">
        <Card>
          <div className="import-icon">
            <UploadCloud />
          </div>
          <h2>Upload a bank file</h2>
          <form onSubmit={upload}>
              {!/\.pdf$/i.test(file?.name || "") && <label>
                Destination account {accounts.length ? "" : "(create an account first)"}
                <select
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={!accounts.length}
                >
                  {accounts.map((a) => (
                    <option value={a.id} key={a.id}>
                      {a.name} · {a.currency}
                    </option>
                  ))}
                </select>
              </label>}
              {/\.pdf$/i.test(file?.name || "") && <Notice tone="info">PDF statements in English, Spanish or Portuguese can contain several accounts. Pisto detects each account locally, suggests matches by account number or IBAN, then lets you map, create or skip each one.</Notice>}
              <label className="drop">
                <FileSpreadsheet />
                <span>
                  {file ? file.name : "Choose PDF / CSV / XLS / XLSX"}
                </span>
                <input
                  type="file"
                  accept=".pdf,.csv,.xls,.xlsx"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </label>
              <button disabled={!file || busy || (!accounts.length && !/\.pdf$/i.test(file?.name || ""))} className="primary full">
                Preview import
              </button>
            </form>
        </Card>
        <Card>
          <div className="import-icon">
            <DatabaseBackup />
          </div>
          <h2>Restore Pisto backup</h2>
          <p className="muted">
            Private files use this installation key. Shareable files require
            their one-time Export Sharing Key.
          </p>
          <label className="drop">
            <RotateCcw />
            <span>{backupFile ? backupFile.name : "Choose .pisto backup"}</span>
            <input
              type="file"
              accept=".pisto,application/json"
              onChange={(e) => setBackupFile(e.target.files[0])}
            />
          </label>
          <label>
            Export Sharing Key (if shareable)
            <input
              type="password"
              minLength="6"
              autoComplete="off"
              value={sharingKey}
              onChange={(e) => setSharingKey(e.target.value)}
            />
          </label>
          <button
            disabled={!backupFile || busy}
            className="secondary full"
            onClick={restore}
          >
            Restore backup
          </button>
        </Card>
      </div>
      <Card>
        <h2>Export movements & backup</h2>
        <div className="export-settings">
          <div className="export-account-row">
            <button
              className={
                exp.accountIds.length === accounts.length
                  ? "filter-pill active"
                  : "filter-pill"
              }
              onClick={() =>
                setExp({
                  ...exp,
                  accountIds: accounts.map((a) => Number(a.id)),
                })
              }
            >
              All accounts
            </button>
            {accounts.map((a) => (
              <button
                key={a.id}
                className={
                  exp.accountIds.includes(Number(a.id))
                    ? "filter-pill active"
                    : "filter-pill"
                }
                onClick={() => toggle(Number(a.id))}
              >
                {a.name}
              </button>
            ))}
          </div>
          <PeriodFilter value={period} onChange={setPeriod} />
          <div className="form-grid compact-grid">
            <label>
              Format
              <select
                value={exp.format}
                onChange={(e) => setExp({ ...exp, format: e.target.value })}
              >
                <option value="pisto">Encrypted Pisto backup</option>
                <option value="csv">CSV / Excel-readable</option>
              </select>
            </label>
            {exp.format === "pisto" && (
              <>
                <label>
                  Backup mode
                  <select
                    value={exp.backupMode}
                    onChange={(e) =>
                      setExp({
                        ...exp,
                        backupMode: e.target.value,
                        sharingKey: "",
                      })
                    }
                  >
                    <option value="private">Private · same installation</option>
                    <option value="shareable">
                      Shareable · another installation/user
                    </option>
                  </select>
                </label>
                {exp.backupMode === "shareable" && (
                  <label className="span2">
                    Export Sharing Key
                    <input
                      type="password"
                      minLength="6"
                      autoComplete="new-password"
                      value={exp.sharingKey}
                      onChange={(e) =>
                        setExp({ ...exp, sharingKey: e.target.value })
                      }
                    />
                    <small>Used for this file only and never stored.</small>
                  </label>
                )}
                <label className="check span2">
                  <input
                    type="checkbox"
                    checked={exp.includeDefinitions}
                    onChange={(e) =>
                      setExp({ ...exp, includeDefinitions: e.target.checked })
                    }
                  />{" "}
                  Include classifications and planning data
                </label>
              </>
            )}
          </div>
          <button className="primary" disabled={busy} onClick={download}>
            <Download size={16} /> Create export
          </button>
        </div>
      </Card>
      <Card>
        <h2>Restore safeguards</h2>
        <p>
          <ShieldCheck size={16} /> Authenticated encryption verifies the
          sharing key and file integrity. Imported private fields are then
          encrypted with the destination installation’s master key.
        </p>
      </Card>
      {preview && (
        <Card className="preview" id="import-preview">
          <div className="page-head compact">
            <h2>Import preview</h2>
            <button className="primary" onClick={commit} disabled={busy || (preview.multiAccount && pdfMappings.some((x) => x.action === "existing" && !x.accountId))}>
              <CheckCircle2 size={17} /> Import new rows
            </button>
          </div>
          {preview.multiAccount && <>
            <Notice tone="info">Detected {preview.summary.accounts} account sections and {preview.summary.total} movements. Account identifiers stay encrypted; only the last four digits are shown.</Notice>
            <div className="pdf-account-mappings">
              {preview.sections.map((section) => {
                const mapping = pdfMappings.find((x) => x.sourceId === section.sourceId) || {};
                return <div className="pdf-account-map" key={section.sourceId}>
                  <div><b>{section.name}</b><small>{section.maskedIdentifier} · {section.rowCount} movements · closing balance {money(section.statementBalance || 0, section.currency)}{section.matchedBy === "account_identifier" ? " · matched by account number / IBAN" : section.matchedBy === "remembered_mapping" ? " · remembered from your earlier import" : section.matchedBy === "account_name" ? " · possible bank/name match" : " · needs your confirmation"}</small>{section.detectedDetails&&<div className="statement-detected-details"><span>Detected:</span>{section.detectedDetails.institution&&<Pill>{section.detectedDetails.institution}</Pill>}{section.detectedDetails.identifier&&<Pill>{section.detectedDetails.identifier}</Pill>}{section.detectedDetails.swift&&<Pill>SWIFT {section.detectedDetails.swift}</Pill>}{section.detectedDetails.accountHolder&&<Pill>{section.detectedDetails.accountHolder}</Pill>}</div>}</div>
                  <label>What should Pisto do?<select value={mapping.action || "skip"} onChange={(e) => updatePdfMapping(section.sourceId, { action: e.target.value })}><option value="existing">Use existing account</option><option value="create">Create new account</option><option value="skip">Skip this account</option></select></label>
                  {mapping.action === "existing" && <label>Destination account<select value={mapping.accountId || ""} onChange={(e) => updatePdfMapping(section.sourceId, { accountId: e.target.value })}><option value="">Choose account</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.currency}</option>)}</select></label>}
                  {mapping.action === "create" && <><label>New account name<input value={mapping.name || ""} onChange={(e) => updatePdfMapping(section.sourceId, { name: e.target.value })}/></label><label>Account type<select value={mapping.accountType || section.accountType} onChange={(e) => updatePdfMapping(section.sourceId, { accountType: e.target.value })}>{["checking","savings","credit_card","cash","loan","other"].map((x) => <option key={x} value={x}>{x.replaceAll("_", " ")}</option>)}</select></label></>}
                  {mapping.action !== "skip"&&<label className="check pdf-account-update"><input type="checkbox" checked={!!mapping.updateAccountDetails} onChange={e=>updatePdfMapping(section.sourceId,{updateAccountDetails:e.target.checked})}/>Add missing statement details to this account{section.suggestedUpdates?.length?<small>{section.suggestedUpdates.map(x=>`${x.field}: ${x.value}`).join(" · ")}</small>:<small>Bank, masked identifier, SWIFT/BIC and account holder when available. Existing values are never overwritten.</small>}</label>}
                </div>;
              })}
            </div>
          </>}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(preview.multiAccount ? preview.sections.flatMap((section) => section.rows.map((row) => ({ ...row, sectionName: section.name, duplicateStatus: "to review", financialType: row.amount >= 0 ? "income" : "expense" }))) : preview.rows).slice(0, 100).map((x, i) => (
                  <tr key={i}>
                    <td>
                      <Pill>{x.duplicateStatus}</Pill>
                    </td>
                    <td>{dateFmt(x.operationDate)}</td>
                    <td>{x.sectionName && <small>{x.sectionName}<br /></small>}{x.description}</td>
                    <td>{x.financialType}</td>
                    <td className="right">{money(x.amount, x.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
